import { createContext, useContext, useState, useCallback, useEffect, ReactNode } from 'react';
import { Language, AppTranslations, appTranslations } from './translations';

const STORAGE_KEY = 'totemium_app_lang';

function getInitialLanguage(): Language {
  try {
    // 1. Check app localStorage
    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && ['fr', 'en', 'es'].includes(stored)) {
      return stored as Language;
    }

    // 2. Check landing page localStorage (sync between landing and app)
    const landingStored = localStorage.getItem('totemium_landing_lang');
    if (landingStored && ['fr', 'en', 'es'].includes(landingStored)) {
      return landingStored as Language;
    }

    // 3. Check browser language
    const browserLang = navigator.language.split('-')[0];
    if (browserLang === 'fr') return 'fr';
    if (browserLang === 'es') return 'es';

    return 'fr';
  } catch {
    return 'fr';
  }
}

interface LanguageContextValue {
  language: Language;
  setLanguage: (lang: Language) => void;
  t: AppTranslations;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: ReactNode }) {
  const [language, setLanguageState] = useState<Language>(getInitialLanguage);

  const setLanguage = useCallback((lang: Language) => {
    setLanguageState(lang);
    try {
      localStorage.setItem(STORAGE_KEY, lang);
      // Sync with landing page key too
      localStorage.setItem('totemium_landing_lang', lang);
      document.documentElement.lang = lang;
    } catch {
      // Ignore storage errors
    }
  }, []);

  useEffect(() => {
    document.documentElement.lang = language;
  }, [language]);

  const t = appTranslations[language];

  return (
    <LanguageContext.Provider value={{ language, setLanguage, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useAppLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) {
    throw new Error('useAppLanguage must be used within a LanguageProvider');
  }
  return ctx;
}
